import { ListItemIcon, ListItemText, Menu, MenuItem } from "@material-ui/core"
import { Cancel, Edit, History, Print, Receipt, Room, Visibility } from "@material-ui/icons"
import { useState } from "react"
import { buscarInformacoesPedido } from "../../services/integracao"
import useAppStore from "../../stores/AppStore"
import { DialogCancelarPedido } from "../DadosPedidoFornecedor/components/DialogCancelarPedido"
import DialogHistoricoPedido from "../PedidosDoDia/DialogHistoricoPedido"
import DialogVerPedido from "../PedidosDoDia/DialogVerPedido"
import DialogEditarPedido from "../RecebedorDePedidos/components/DialogEditarPedido"
import DialogEmissaoFiscal from "../RecebedorDePedidos/components/DialogEmissaoFiscal"
import { verificarPrintQRCode } from "../RecebedorDePedidos/functions"

/**
 * Menu de ações de um pedido (⋮) + os diálogos de cada ação: visualizar, reimprimir,
 * editar, cancelar, histórico, mapa e cupom fiscal. Uma única instância por tela: o pai
 * guarda `menu` = { anchor, pedido } da linha clicada.
 *
 * `pedido` é normalizado pelo pai:
 *   { id, cliente, plataforma, latitude, longitude, podeCancelar }
 *
 * Props:
 *  - menu, onFecharMenu
 *  - autorizacao
 *  - fiscalAtivo: exibe a ação de cupom fiscal
 *  - onAlterado(): pedido editado/cancelado — o pai recarrega a lista
 *  - alertStart(msg, tipo)
 */
export default function MenuAcoesPedido({
    menu,
    onFecharMenu,
    autorizacao,
    fiscalAtivo,
    onAlterado,
    alertStart,
}) {
    const [pedidoVer, setPedidoVer] = useState(null)
    const [idHistorico, setIdHistorico] = useState(null)
    const [idEditar, setIdEditar] = useState(null)
    const [pedidoCancelar, setPedidoCancelar] = useState(null)
    const [pedidoFiscal, setPedidoFiscal] = useState(null)

    const pedido = menu?.pedido
    const temCoordenada = !!Number(pedido?.latitude) && !!Number(pedido?.longitude)

    const executar = acao => () => {
        const alvo = menu?.pedido
        onFecharMenu()
        if (alvo) acao(alvo)
    }

    const reimprimir = async p => {
        const info = await buscarInformacoesPedido(autorizacao, p.id)
        if (!info?.id) {
            alertStart("Não foi possível carregar o pedido para impressão.", "error")
            return
        }
        useAppStore.getState().mandarImprimir({
            pedido: info,
            texto: info.texto,
            qtd: 1,
            qrcode: verificarPrintQRCode(info),
            imprimirNota: true,
            reimpressaoManual: true,
        })
        alertStart(`Pedido #${p.id} enviado para impressão.`, "info")
    }

    const abrirMapa = p => {
        window.open(
            `https://www.google.com/maps/search/?api=1&query=${p.latitude},${p.longitude}`,
            "_blank",
            "noopener"
        )
    }

    return (
        <>
            <Menu
                anchorEl={menu?.anchor ?? null}
                open={!!menu}
                onClose={onFecharMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                getContentAnchorEl={null}
            >
                <MenuItem onClick={executar(p => setPedidoVer({ id: p.id }))}>
                    <ListItemIcon>
                        <Visibility fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Visualizar pedido" />
                </MenuItem>
                <MenuItem onClick={executar(reimprimir)}>
                    <ListItemIcon>
                        <Print fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Reimprimir" />
                </MenuItem>
                <MenuItem onClick={executar(p => setIdEditar(p.id))}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Editar pedido" />
                </MenuItem>
                {pedido?.podeCancelar && (
                    <MenuItem onClick={executar(p => setPedidoCancelar(p))}>
                        <ListItemIcon>
                            <Cancel fontSize="small" style={{ color: "#dc3545" }} />
                        </ListItemIcon>
                        <ListItemText primary="Cancelar pedido" />
                    </MenuItem>
                )}
                <MenuItem onClick={executar(p => setIdHistorico(p.id))}>
                    <ListItemIcon>
                        <History fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Ver histórico" />
                </MenuItem>
                {temCoordenada && (
                    <MenuItem onClick={executar(abrirMapa)}>
                        <ListItemIcon>
                            <Room fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Ver no mapa" />
                    </MenuItem>
                )}
                {fiscalAtivo && (
                    <MenuItem onClick={executar(p => setPedidoFiscal(p))}>
                        <ListItemIcon>
                            <Receipt fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Nota Fiscal Consumidor" />
                    </MenuItem>
                )}
            </Menu>

            <DialogVerPedido
                pedido={pedidoVer}
                acoes={[]}
                autorizacao={autorizacao}
                onAcao={() => undefined}
                onClose={() => setPedidoVer(null)}
            />
            <DialogHistoricoPedido
                aberto={idHistorico != null}
                pedidoId={idHistorico}
                autorizacao={autorizacao}
                onClose={() => setIdHistorico(null)}
            />
            <DialogEditarPedido
                idPedido={idEditar}
                authorization={autorizacao}
                onFechar={() => setIdEditar(null)}
                onSalvo={() => {
                    setIdEditar(null)
                    onAlterado()
                }}
            />
            <DialogCancelarPedido
                pedido={pedidoCancelar}
                onFechar={() => setPedidoCancelar(null)}
                onCancelado={() => {
                    setPedidoCancelar(null)
                    onAlterado()
                }}
                alertStart={alertStart}
            />
            <DialogEmissaoFiscal
                open={!!pedidoFiscal}
                pedido={pedidoFiscal || {}}
                onClose={() => setPedidoFiscal(null)}
                alertStart={alertStart}
            />
        </>
    )
}
